// miniapp/src/components/chat/MessageActionModal.tsx
//
// v4.1 milestone — Telegram-style action menu shown when the technician
// long-presses a message bubble. The held bubble itself is cloned via its
// captured outerHTML and rendered ABOVE the blurred backdrop, so the user
// can clearly see which message they're acting on. The menu hangs directly
// beneath the cloned bubble, anchored to the same horizontal edge.
//
// Single-step menu: Select / Delete. "Delete" is disabled when the message
// can't be unsent on Telegram (user-sent, missing telegram_message_id, or
// >48 h old) — the disabled label carries an inline tooltip explaining
// why. Tap outside or Escape closes the modal.
//
// Why a DOM clone and not <MessageBubble /> re-rendered: avoids re-coupling
// the modal to the bubble's prop derivation logic (isMine / senderLabel /
// showLabelOnMine / ticket dividers context) AND guarantees pixel-perfect
// visual match with the bubble the user actually pressed. Tailwind classes
// are global so the cloned markup styles correctly.
//
// Layout strategy:
//   - Bubble stays at its original screen position when it fits
//   - Shifts up only when the menu wouldn't fit below
//   - Scales down (preserving aspect ratio) when the bubble itself is too
//     tall to share the viewport with the menu

import { useEffect, useMemo, useState } from 'react';
import type { Message } from '../../types/ticket';
import { MEDIA_PLACEHOLDERS, NON_MEDIA_LABEL_TOKENS } from './MediaContent';

const MENU_WIDTH_PX = 220;
const ANCHOR_GAP_PX = 8;
const VIEWPORT_PADDING_TOP_PX = 12;
const VIEWPORT_PADDING_SIDE_PX = 12;
// Minimum breathing room between the menu's bottom edge and the viewport
// bottom. The actual bottom inset adds env(safe-area-inset-bottom) on top
// (iOS home indicator etc.) — read at layout time below.
const VIEWPORT_PADDING_BOTTOM_PX = 24;
const MIN_SCALE = 0.5;
const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000;

/** Reads env(safe-area-inset-bottom) by temporarily mounting a probe div.
 *  We can't `getComputedStyle(env(...))` directly, so we let the browser
 *  resolve it via padding-bottom and read back the pixel value. Falls
 *  back to 0 when the env var isn't supported. */
function readSafeAreaInsetBottom(): number {
  if (typeof document === 'undefined') return 0;
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;left:-9999px;top:-9999px;padding-bottom:env(safe-area-inset-bottom,0px);';
  document.body.appendChild(probe);
  const px = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
  document.body.removeChild(probe);
  return px;
}

export interface MessageActionModalProps {
  /** Bounding box of the held bubble at gesture time. */
  anchorRect: DOMRect;
  /** outerHTML of the bubble at gesture time — rendered above the backdrop. */
  bubbleHtml: string;
  /** True if the held bubble is right-aligned (technician's own message). */
  isMineSide: boolean;
  /** The held message — used to gate Delete eligibility (technician-sent +
   *  telegram_message_id present + ≤48 h old). */
  message: Message;
  /** Viewer's device id — used by the Edit eligibility check to decide
   *  whether this viewer is the message's author. L1 techs can only edit
   *  their own; L2 techs can edit any (handled via `viewerLevel`). */
  viewerDeviceId?: string;
  /** Viewer's privilege tier (technician_profiles.level). When >=2 the
   *  Edit action appears on any technician message regardless of author. */
  viewerLevel?: number;
  /** Phase 33: open Reply mode pre-seeded with the held message as the
   *  reply target. Caller surfaces the composer's quoted-preview bar
   *  on the next render. Only invoked when the Reply button renders —
   *  the modal gates rendering on `message.telegramMessageId != null`. */
  onReply: () => void;
  /** Edit-message phase: open Edit mode pre-seeded with the held message
   *  as the edit target. Caller surfaces the composer in edit mode
   *  (pre-filled text + PATCH-on-send) on the next render. Only invoked
   *  when the Edit button renders — gated by the eligibility memo below. */
  onEdit: () => void;
  /** Open Select multi-select mode pre-seeded with the held message. */
  onSelect: () => void;
  /** Commit the delete — caller handles the API call + WS sync. */
  onConfirmDelete: () => void;
  /** Backdrop tap or Escape. */
  onClose: () => void;
}

interface Layout {
  cloneTop: number;
  cloneLeft: number;
  scale: number;
  menuTop: number;
  menuLeft: number;
}

function computeLayout(
  anchor: DOMRect,
  menuHeight: number,
  isMineSide: boolean,
  safeAreaBottom: number
): Layout {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const bubbleH = anchor.height;
  const bubbleW = anchor.width;

  // Effective bottom buffer = safe-area inset (home indicator) + breathing
  // room. This is what we reserve below the menu's bottom edge — it's
  // why long-pressing a near-bottom message shifts the bubble upward
  // instead of letting the menu collide with the home indicator.
  const bottomBuffer = safeAreaBottom + VIEWPORT_PADDING_BOTTOM_PX;

  const availForBubble = vh - VIEWPORT_PADDING_TOP_PX - bottomBuffer - ANCHOR_GAP_PX - menuHeight;

  let scale: number;
  let cloneTop: number;
  if (bubbleH <= availForBubble) {
    scale = 1;
    const desiredTop = anchor.top;
    const maxTop = vh - bubbleH - ANCHOR_GAP_PX - menuHeight - bottomBuffer;
    cloneTop = Math.max(VIEWPORT_PADDING_TOP_PX, Math.min(desiredTop, maxTop));
  } else {
    scale = Math.max(MIN_SCALE, availForBubble / bubbleH);
    cloneTop = VIEWPORT_PADDING_TOP_PX;
  }

  const cloneLeft = anchor.left;
  const scaledH = bubbleH * scale;
  const scaledW = bubbleW * scale;
  const menuTop = cloneTop + scaledH + ANCHOR_GAP_PX;
  let menuLeft: number;
  if (isMineSide) {
    const rightEdge = cloneLeft + scaledW;
    menuLeft = rightEdge - MENU_WIDTH_PX;
  } else {
    menuLeft = cloneLeft;
  }
  menuLeft = Math.max(
    VIEWPORT_PADDING_SIDE_PX,
    Math.min(menuLeft, vw - MENU_WIDTH_PX - VIEWPORT_PADDING_SIDE_PX)
  );

  return { cloneTop, cloneLeft, scale, menuTop, menuLeft };
}

export function MessageActionModal({
  anchorRect,
  bubbleHtml,
  isMineSide,
  message,
  viewerDeviceId,
  viewerLevel,
  onReply,
  onEdit,
  onSelect,
  onConfirmDelete,
  onClose,
}: MessageActionModalProps) {
  // Delete eligibility: technician-sent + telegram_message_id + ≤48 h old.
  // Anything else can't be unsent on Telegram, so we refuse the delete
  // entirely (no local-only soft-delete fallback).
  const deletable = useMemo(
    () =>
      message.from === 'technician' &&
      message.telegramMessageId != null &&
      Date.now() - new Date(message.timestamp).getTime() <= FORTY_EIGHT_HOURS_MS,
    [message]
  );
  const ineligibleReason = useMemo(() => {
    if (message.from === 'user') return "Can't delete — user's message can't be removed from Telegram.";
    if (message.telegramMessageId == null) return "Can't delete — sent before this feature shipped.";
    return "Can't delete — older than 48 hours.";
  }, [message]);

  // Phase 33: Reply visibility. Per CONTEXT.md Locked Decisions, we HIDE
  // (not disable) the Reply action when there's no telegram_message_id
  // to attach to reply_parameters. This applies to historical rows
  // predating migration 018 — they have no Telegram-side anchor to
  // quote. Distinct from `deletable` above (which is technician-side +
  // ≤48h); a USER message that has a telegram_message_id IS replyable.
  const replyable = useMemo(
    () => message.telegramMessageId != null,
    [message]
  );

  // Edit eligibility — mirrors the server's PATCH /tickets/.../messages/:id
  // permission gate so the UI never surfaces a button that the server
  // would 403/400 anyway:
  //   - technician-sent (user rows can't be edited via bot API)
  //   - telegram_message_id present
  //   - ≤48 h old
  //   - (technicianId === viewerDeviceId) OR (viewerLevel >= 2)
  // The Edit button is HIDDEN when not editable (no disabled-with-tooltip
  // affordance — keep the modal tight; the user wouldn't expect Edit on
  // someone else's bubble in the L1 case).
  const editable = useMemo(() => {
    if (message.from !== 'technician') return false;
    if (message.telegramMessageId == null) return false;
    if (Date.now() - new Date(message.timestamp).getTime() > FORTY_EIGHT_HOURS_MS) return false;
    const isAuthor = viewerDeviceId != null && String(message.technicianId ?? '') === viewerDeviceId;
    const isL2 = (viewerLevel ?? 1) >= 2;
    return isAuthor || isL2;
  }, [message, viewerDeviceId, viewerLevel]);

  // Copy eligibility: message has real non-empty text that is not a
  // media placeholder ('[photo]', '[voice]', …) and not a non-media
  // label token ('[location]', '[poll]', '[dice]', '[message]').
  // Reuses the same MEDIA_PLACEHOLDERS / NON_MEDIA_LABEL_TOKENS sets
  // that MessageBubble.tsx uses for hasText / isLabel checks so the
  // gate stays in sync automatically when new tokens are added there.
  const copyable = useMemo(() => {
    const t = (message.text ?? '').trim();
    if (!t) return false;
    if (MEDIA_PLACEHOLDERS.has(t)) return false;
    if (NON_MEDIA_LABEL_TOKENS.has(t)) return false;
    return true;
  }, [message]);

  // Clipboard write with InAppWebView fallback.
  // navigator.clipboard.writeText may be blocked or undefined inside the
  // Flutter InAppWebView; fall back to a hidden <textarea> +
  // document.execCommand('copy'). Menu closes (onClose) regardless of
  // copy outcome — closing IS the minimal user feedback (no toast system
  // exists in this codebase).
  const handleCopy = async () => {
    const text = message.text ?? '';
    let copied = false;
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        copied = true;
      } catch {
        // fall through to execCommand path
      }
    }
    if (!copied) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        // best-effort; still close the menu
      }
    }
    onClose();
  };

  // Menu height is measured after the first paint; layout recomputes once.
  // Generous initial estimate covers ~2 rows of buttons + chrome.
  const [menuHeight, setMenuHeight] = useState<number>(110);
  // env(safe-area-inset-bottom) is read once on mount — the value is a
  // device characteristic, not something that changes during the modal's
  // lifetime. Used as the reserved buffer beneath the menu so it can't
  // collide with the home indicator.
  const safeAreaBottom = useMemo(() => readSafeAreaInsetBottom(), []);
  const layout = useMemo(
    () => computeLayout(anchorRect, menuHeight, isMineSide, safeAreaBottom),
    [anchorRect, menuHeight, isMineSide, safeAreaBottom]
  );

  // Fade-in + subtle scale on mount. rAF defers the second state to the
  // next paint so the initial 'mounted=false' style commits and the
  // transition runs — without it, React batches and the modal pops in.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Layered transition: backdrop fades in, bubble clone fades in (same
  // transform-origin as scale-to-fit so it doesn't fight), menu scales up
  // from 0.92 to 1 with a slight upward translate.
  const bubbleScale = (layout.scale !== 1 ? layout.scale : 1) * (mounted ? 1 : 0.98);
  return (
    <div
      className="fixed inset-0 z-50"
      onClick={onClose}
      style={{
        backdropFilter: mounted ? 'blur(10px)' : 'blur(0px)',
        WebkitBackdropFilter: mounted ? 'blur(10px)' : 'blur(0px)',
        background: mounted ? 'rgba(0, 0, 0, 0.30)' : 'rgba(0, 0, 0, 0)',
        transition:
          'background 200ms ease-out, backdrop-filter 200ms ease-out, -webkit-backdrop-filter 200ms ease-out',
        touchAction: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    >
      {/* Cloned bubble — visual only. */}
      <div
        className="absolute"
        style={{
          top: layout.cloneTop,
          left: layout.cloneLeft,
          width: anchorRect.width,
          transform: bubbleScale !== 1 ? `scale(${bubbleScale})` : undefined,
          transformOrigin: isMineSide ? 'top right' : 'top left',
          opacity: mounted ? 1 : 0,
          transition: 'opacity 180ms ease-out, transform 220ms cubic-bezier(0.32, 0.72, 0, 1)',
          pointerEvents: 'none',
        }}
        dangerouslySetInnerHTML={{ __html: bubbleHtml }}
      />

      {/* Action menu. */}
      <div
        ref={(el) => {
          if (el) {
            const h = el.getBoundingClientRect().height;
            if (h && Math.abs(h - menuHeight) > 1) setMenuHeight(h);
          }
        }}
        className="absolute rounded-2xl bg-white shadow-xl overflow-hidden"
        style={{
          top: layout.menuTop,
          left: layout.menuLeft,
          width: MENU_WIDTH_PX,
          transform: mounted ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(-6px)',
          transformOrigin: isMineSide ? 'top right' : 'top left',
          opacity: mounted ? 1 : 0,
          transition:
            'transform 220ms cubic-bezier(0.32, 0.72, 0, 1), opacity 180ms ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {copyable && (
          <button
            type="button"
            onClick={() => { void handleCopy(); }}
            className="w-full px-4 py-3.5 text-left text-base text-text-primary hover:bg-gray-50 active:bg-gray-100 border-b border-border-secondary"
          >
            Copy
          </button>
        )}
        {replyable && (
          <button
            type="button"
            onClick={onReply}
            className="w-full px-4 py-3.5 text-left text-base text-text-primary hover:bg-gray-50 active:bg-gray-100 border-b border-border-secondary"
          >
            Reply
          </button>
        )}
        {editable && (
          <button
            type="button"
            onClick={onEdit}
            className="w-full px-4 py-3.5 text-left text-base text-text-primary hover:bg-gray-50 active:bg-gray-100 border-b border-border-secondary"
          >
            Edit
          </button>
        )}
        <button
          type="button"
          onClick={onSelect}
          className="w-full px-4 py-3.5 text-left text-base text-text-primary hover:bg-gray-50 active:bg-gray-100 border-b border-border-secondary"
        >
          Select
        </button>
        <button
          type="button"
          disabled={!deletable}
          onClick={deletable ? onConfirmDelete : undefined}
          title={deletable ? undefined : ineligibleReason}
          className={`w-full px-4 py-3.5 text-left text-base ${
            deletable
              ? 'text-red-500 hover:bg-gray-50 active:bg-gray-100'
              : 'text-gray-400 cursor-not-allowed'
          }`}
        >
          Delete
        </button>
        {!deletable && (
          <p className="px-4 pb-2.5 pt-0 text-left text-[11px] text-text-tertiary leading-snug">
            {ineligibleReason}
          </p>
        )}
      </div>
    </div>
  );
}
